


import React, { useState } from "react";
import { Transition } from "@headlessui/react"; // For smooth animations

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(index === openIndex ? null : index);
  };

  const faqs = [
    {
      title: 'Is a visa required for UAE residents to visit the USA?',
      content: (
        <p>
          Yes, all UAE residents must obtain a visa to travel to the United States, regardless of the purpose of their visit, whether for tourism, business, or immigration.
        </p>
      ),
    },
    {
      title: 'What documents are necessary for a USA visitor visa application?',
      content: (
        <ul className="list-disc list-inside">
          <li>Completed DS-160 form confirmation page.</li>
          <li>A valid passport with at least six months validity and a blank page for the visa sticker.</li>
          <li>Recent photograph for US visa (5x5 cm, taken within the past 6 months).</li>
          <li>Confirmation of the visa appointment (if applicable).</li>
          <li>Receipt of payment for the visa application fee.</li>
          <li>Previous US visas (if applicable).</li>
          <li>Police clearance or certificate verifying no criminal history.</li>
          <li>Any documents relating to previous visa rejections or deportations (if applicable).</li>
        </ul>
      ),
    },
    {
      title: 'What is the DS-160 form?',
      content: (
        <p>
          The DS-160 form is an online application for a Nonimmigrant Visa for temporary stays in the USA. The completed form is submitted electronically and serves as the basis for your visa application process.
        </p>
      ),
    },
    {
      title: 'Which documents are required to complete the DS-160 form?',
      content: (
        <ul className="list-disc list-inside">
          <li>Travel details (if travel arrangements are confirmed).</li>
          <li>Information about your last five US visits and international travel in the past five years.</li>
          <li>Updated resume or CV detailing your educational and professional background.</li>
          <li>Additional documents may be needed depending on the purpose of travel.</li>
        </ul>
      ),
    },
    {
      title: 'What is Form I-20 and how is it obtained?',
      content: (
        <p>
          Form I-20 is issued by approved educational institutions in the USA for students applying for F-1 or M-1 visas. This form serves as evidence of acceptance to the institution and is essential for paying the SEVIS I-901 fee.
        </p>
      ),
    },
    {
      title: 'What additional documents are needed for DS-160 completion?',
      content: (
        <ul className="list-disc list-inside">
          <li>
            <strong>For Students and Exchange Visitors (F, J, M):</strong> SEVIS ID from the I-20 or DS-2019 form and information about the school.
          </li>
          <li>
            <strong>For Petition-Based Temporary Workers (H, L, O, etc.):</strong> Copy of the I-129 form.
          </li>
          <li>
            <strong>For Other Temporary Workers:</strong> Employment history, including contact details of previous employers.
          </li>
        </ul>
      ),
    },
    {
      title: 'What documents are necessary for a US tourist visa application?',
      content: (
        <ul className="list-disc list-inside">
          <li>Cover letter explaining the trip's purpose.</li>
          <li>Financial documents or bank statements showing sufficient funds to cover the trip.</li>
          <li>Evidence of strong ties to Dubai (family, job, property, etc.).</li>
          <li>Invitation letter (if applicable).</li>
          <li>For students, transcripts or diplomas.</li>
          <li>For employees, a letter from your employer and recent pay slips (typically the last 3 months).</li>
        </ul>
      ),
    },
    {
      title: 'Is attending a visa interview compulsory?',
      content: (
        <p>
          Yes, all UAE residents aged 14-79 are required to attend a visa interview. It is essential to be on time and provide truthful, consistent responses in alignment with your submitted documents.
        </p>
      ),
    },
    {
      title: 'How long does it typically take to process a visa from Dubai?',
      content: (
        <p>
          The standard processing time for a US visa from Dubai is approximately four to six weeks.
        </p>
      ),
    },
    {
      title: 'What steps should I take if my visa is denied?',
      content: (
        <p>
          If your visa application is denied, you can reapply by submitting a new application, including paying the necessary fees again. Be sure to verify that all documents and information are complete and accurate.
        </p>
      ),
    },
    {
      title: 'What is merit-based immigration to the USA?',
      content: (
        <p>
          Merit-based immigration is a system where applicants earn points based on factors such as education, work skills and employment prospects. The RAISE Act is a proposed framework that would implement this system.
        </p>
      ),
    },
    {
      title: 'What health insurance options are available to immigrants in the USA?',
      content: (
        <p>
          Immigrants in the USA are required to secure health insurance through private providers, as the country does not have a universal healthcare system that covers all individuals.
        </p>
      ),
    },
    {
      title: 'Which countries can Green Card holders visit without needing a visa?',
      content: (
        <p>
          Green Card holders can travel to several countries without additional visa requirements, including Canada, Mexico, Belize, Costa Rica and Jamaica.
        </p>
      ),
    },
    {
      title: 'What is Section 214(b) and how does it impact visa applications?',
      content: (
        <p>
          Section 214(b) presumes every visa applicant is an immigrant unless they can prove otherwise. If an applicant fails to establish strong ties to their home country, their visa may be denied under this section.
        </p>
      ),
    },
    {
      title: 'How can one demonstrate strong ties to their home country?',
      content: (
        <ul className="list-disc list-inside">
          <li>Employment or ownership of a business.</li>
          <li>Real estate ownership or long-term lease agreements.</li>
          <li>Close family members living in the home country.</li>
          <li>Financial ties, such as bank accounts.</li>
        </ul>
      ),
    },
    {
      title: 'What is a petition and why is it necessary?',
      content: (
        <p>
          A petition is a formal request submitted by a potential employer or sponsor on behalf of the applicant (typically using Form I-129 for workers). This is required before applying for specific visa categories.
        </p>
      ),
    },
    {
      title: 'How long can I stay in the USA on a tourist or business visa?',
      content: (
        <p>
          The duration of your stay in the USA on a tourist or business visa is determined by the Customs and Border Protection officer upon arrival, regardless of the visa's expiration date.
        </p>
      ),
    },
    {
      title: 'What should I do if my visa is damaged?',
      content: (
        <p>
          If your visa is damaged, you must apply for a new visa at the US Embassy or Consulate abroad.
        </p>
      ),
    },
    {
      title: 'Is it possible to travel with an expired passport and a valid visa?',
      content: (
        <p>
          Yes, you can travel with two passports (an old and a new one), as long as your visa remains valid and undamaged and the personal details in both passports are consistent.
        </p>
      ),
    },
    // Add more FAQs here as needed...
  ];

  const [isOpen, setIsOpen] = useState(false);

  const toggleAnswer = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className="flex items-center justify-center py-20 w-full flex-col">
        {isOpen && (
          <div className="mb-6 bg-gray-100 p-4 w-3/4 text-black items-center justify-center text-lg rounded-md shadow-md">
            <p>
              <br />
              - Why is the USA considered the "Land of Opportunities"?<br />
              The USA offers a stable economy, high-paying jobs and an environment for innovation and entrepreneurship.<br />
              <br />
              - What makes the USA a preferred choice for skilled professionals?<br />
              A transparent and merit-based immigration system, coupled with abundant career opportunities in diverse industries.<br />
              <br />
              - How does the USA ensure a high quality of life?<br />
              It boasts excellent healthcare, top-notch education and a culturally vibrant society that supports diversity.<br />
              <br />
              - What are the educational advantages in the USA?<br />
              The USA is home to world-renowned universities and offers extensive support for students with advanced degrees.<br />
              <br />
              - Why is the USA a global leader in technology?<br />
              The country is at the forefront of innovation with leading companies like Apple, Microsoft and IBM driving the tech industry.<br />
              Embark on your journey to the USA and unlock endless possibilities with Visa World!
            </p>
          </div>
        )}
        <button
          onClick={toggleAnswer}
          className="bg-blue-600 text-white text-center text-md justify-center font-semibold py-3 md:w-1/2 w-2/2 px-6 rounded-full hover:bg-blue-700 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 flex items-center"
        >
          Why Choose USA?
          <svg
            className={`ml-3 w-4 h-4 transform transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14M5 12l7 7 7-7"></path>
          </svg>
        </button>
      </div>

      <div className="mx-auto max-w-5xl p-6 bg-white rounded-lg">
        <h2 className="text-4xl text-center font-semibold text-[#011473] mb-4">
          FAQ's
        </h2>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        {faqs.map((faq, index) => (
          <div key={index} className="mb-4 border rounded-lg overflow-hidden shadow-sm">
            <button
              className={`w-full flex justify-between items-center text-left px-4 py-3 font-semibold ${
                openIndex === index ? "bg-blue-600 text-white" : "bg-gray-100"
              }`}
              onClick={() => toggleFAQ(index)}
            >
              {faq.title}
              <span
                className={`transform transition-transform ${
                  openIndex === index ? "rotate-180" : "rotate-0"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </button>
            <Transition
              show={openIndex === index}
              enter="transition-all duration-200 ease-out"
              enterFrom="max-h-0 opacity-0"
              enterTo="max-h-96 opacity-100"
              leave="transition-all duration-200 ease-in"
              leaveFrom="max-h-96 opacity-100"
              leaveTo="max-h-0 opacity-0"
            >
              <div className="px-4 py-3 bg-white text-gray-700">
                {faq.content}
              </div>
            </Transition>
          </div>
        ))}
      </div>
    </>
  );
};

export default FAQ;
